import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { NinaForm } from '../../components/NinaForm.js';
import { Button } from '../../components/ui/Button.js';
import { Card, CardBody, CardHeader, CardTitle } from '../../components/ui/Card.js';
import { FwaApiError } from '../../lib/api.js';
import { useNina, useUpdateNina } from '../../lib/queries/ninas.js';

export function NinasEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: ninaResp, isLoading: loadingNina, refetch } = useNina(id);
  const mutation = useUpdateNina();
  const [serverError, setServerError] = useState<string>('');
  const [showConflictModal, setShowConflictModal] = useState(false);

  if (loadingNina) {
    return <div className="text-center py-8 text-gray-600">Cargando…</div>;
  }
  if (!ninaResp || !id) {
    return (
      <Card>
        <CardBody>
          <p className="text-gray-600">Niña no encontrada.</p>
          <Link to="/ninas">
            <Button variant="ghost" className="mt-4">
              ← Regresar al padrón
            </Button>
          </Link>
        </CardBody>
      </Card>
    );
  }

  const nina = ninaResp.data;
  const etag = ninaResp.etag ?? nina.version_id;

  return (
    <div className="space-y-4 max-w-2xl">
      <h1 className="text-2xl font-semibold text-gray-900">Editar niña</h1>
      <p className="text-sm text-gray-500">
        Folio: <span className="font-mono">{nina.folio_gdm}</span> · Versión actual:{' '}
        <span className="font-mono text-xs">{nina.version_id}</span>
      </p>

      <Card>
        <CardHeader>
          <CardTitle>Datos editables</CardTitle>
        </CardHeader>
        <CardBody>
          <NinaForm
            mode="edit"
            initialValues={{
              nombre_completo: nina.nombre_completo,
              tutor_nombre: nina.tutor_nombre,
              tutor_email: nina.tutor_email,
              tutor_telefono: nina.tutor_telefono,
              rama_actual: nina.rama_actual,
            }}
            onSubmit={(payload) => {
              setServerError('');
              mutation.mutate(
                { id, payload, etag },
                {
                  onSuccess: (res) => {
                    if ('queuedOffline' in res) {
                      navigate('/outbox');
                      return;
                    }
                    navigate(`/ninas/${res.data.entity_id}`);
                  },
                  onError: (err) => {
                    if (err instanceof FwaApiError) {
                      if (err.status === 412) {
                        setShowConflictModal(true);
                        return;
                      }
                      setServerError(err.problem.detail ?? err.problem.title);
                    } else {
                      setServerError(err.message);
                    }
                  },
                },
              );
            }}
            isSubmitting={mutation.isPending}
            serverError={serverError}
          />
        </CardBody>
      </Card>

      {showConflictModal && (
        <dialog
          open
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 max-w-none w-full h-full"
          aria-labelledby="conflict-title"
        >
          <Card className="max-w-md mx-4">
            <CardHeader>
              <CardTitle>
                <span id="conflict-title">Conflicto de versión</span>
              </CardTitle>
            </CardHeader>
            <CardBody>
              <p>
                Otra persona editó esta niña mientras tú estabas viendo. Recarga para ver los
                cambios más recientes y vuelve a aplicar tus cambios.
              </p>
              <div className="flex gap-3 mt-4">
                <Button
                  variant="primary"
                  onClick={() => {
                    setShowConflictModal(false);
                    void refetch();
                  }}
                >
                  Recargar y re-intentar
                </Button>
                <Button variant="ghost" onClick={() => navigate(`/ninas/${id}`)}>
                  Descartar cambios
                </Button>
              </div>
            </CardBody>
          </Card>
        </dialog>
      )}

      <Link to={`/ninas/${id}`}>
        <Button variant="ghost">← Cancelar edición</Button>
      </Link>
    </div>
  );
}
